import React, { useState } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Card, CardContent } from './ui/card';
import { toast } from 'sonner';
import { Send, Package, Calendar, Users, ShoppingCart } from 'lucide-react';
import { useCart } from '../context/CartContext';
import { whatsappOrderNumber } from '../data/mockData';
import { trackEvent } from '@/lib/analytics';

const OrderForm = () => {
  const { cartForOrder } = useCart();
  const [formData, setFormData] = useState({
    name: '',
    address: '',
    phone: '',
    deliveryDate: '',
    message: ''
  });

  const formatFlavors = (flavors = []) => {
    if (!flavors.length) {
      return 'Sem selecao de sabores';
    }

    const flavorCount = flavors.reduce((acc, flavor) => {
      acc[flavor] = (acc[flavor] || 0) + 1;
      return acc;
    }, {});

    return Object.entries(flavorCount)
      .map(([flavor, count]) => (count > 1 ? `${flavor} (${count})` : flavor))
      .join(', ');
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const submitStart = performance.now();
    
    // Validation
    if (!formData.name || !formData.phone) {
      toast.error('Por favor, preencha todos os campos obrigatórios');
      return;
    }

    if (!cartForOrder?.items?.length) {
      toast.error('Adicione produtos ao carrinho antes de enviar o pedido');
      return;
    }

     // Prepare complete order data
     const completeOrder = {
       ...formData,
       products: cartForOrder?.items || [],
       total: cartForOrder?.total || '0.00'
     };

     const itemsText = completeOrder.products
       .map((item, index) => {
         return `${index + 1}. ${item.productType}\n` +
           `- Quantidade: ${item.quantity} unidades\n` +
           `- Sabores: ${formatFlavors(item.flavors)}\n` +
           `- Valor: ${item.price}`;
       })
       .join('\n\n');

     const message =
       `Olá! Gostaria de finalizar o meu pedido.\n\n` +
       `Dados do cliente:\n` +
       `- Nome: ${completeOrder.name}\n` +
       `- Telefone: ${completeOrder.phone}\n` +
      `- Morada: ${completeOrder.address || 'Não informada'}\n` +
       `- Data de entrega: ${completeOrder.deliveryDate || 'Não informada'}\n` +
       `- Observações: ${completeOrder.message || 'Sem observações'}\n\n` +
       `Resumo do pedido:\n${itemsText}\n\n` +
       `Total: €${completeOrder.total}`;

     const orderTotal = Number(String(completeOrder.total).replace(/[^\d,.-]/g, '').replace(',', '.')) || 0;

     trackEvent('order_form_submitted', {
       currency: 'EUR',
       value: orderTotal,
       items: completeOrder.products.length,
     });

     const whatsappUrl = `https://wa.me/${whatsappOrderNumber}?text=${encodeURIComponent(message)}`;

     trackEvent('whatsapp_order_click', {
       currency: 'EUR',
       value: orderTotal,
       items: completeOrder.products.length,
     });

     // Opens WhatsApp with the order message pre-filled
     const whatsappWindow = window.open(whatsappUrl, '_blank');
     const openLatencyMs = Math.round(performance.now() - submitStart);

     if (whatsappWindow) {
       trackEvent('whatsapp_window_opened', {
         currency: 'EUR',
         value: orderTotal,
         items: completeOrder.products.length,
         open_latency_ms: openLatencyMs,
       });
     } else {
       trackEvent('whatsapp_window_blocked', {
         currency: 'EUR',
         value: orderTotal,
         items: completeOrder.products.length,
         open_latency_ms: openLatencyMs,
       });
     }

     // Keep console log for debugging
     console.log('Pedido enviado:', completeOrder);
    toast.success('A abrir WhatsApp com o seu pedido...');
    
    // Reset form
    setFormData({
      name: '',
      address: '',
      phone: '',
      deliveryDate: '',
      message: ''
    });
  };

  return (
    <section id="order" className="py-20 bg-gradient-to-b from-amber-50 to-white">
      <div className="container mx-auto px-4">
        <div className="max-w-4xl mx-auto">
          {/* Section header */}
          <div className="text-center mb-12">
            <Badge className="mb-4 bg-amber-100 text-amber-800 hover:bg-amber-200">
              Faça Seu Pedido
            </Badge>
            <h2 className="text-4xl md:text-5xl font-bold text-amber-950 mb-4">
              Encomende Agora
            </h2>
            <p className="text-xl text-amber-700">
              Preencha o formulário e entraremos em contato para confirmar seu pedido
            </p>
          </div>

          {/* Order Summary */}
          {cartForOrder && cartForOrder.items && cartForOrder.items.length > 0 && (
            <div className="mb-8 p-6 bg-amber-50 border-2 border-amber-200 rounded-lg">
              <div className="flex items-center gap-2 mb-4">
                <ShoppingCart className="w-5 h-5 text-amber-700" />
                <h3 className="text-lg font-bold text-amber-950">Resumo do Seu Pedido</h3>
              </div>
              <div className="space-y-3">
                {cartForOrder.items.map((item, idx) => (
                  <div key={idx} className="flex justify-between items-start pb-3 border-b border-amber-200 last:border-0">
                    <div>
                      <p className="font-semibold text-amber-950">{item.productType}</p>
                      <p className="text-sm text-amber-700">
                        Quantidade: {item.quantity} unidades
                      </p>
                      <p className="text-sm text-amber-600">
                        Sabores: {formatFlavors(item.flavors)}
                      </p>
                    </div>
                    <p className="font-bold text-amber-700">{item.price}</p>
                  </div>
                ))}
                <div className="pt-3 border-t-2 border-amber-300 flex justify-between items-center">
                  <p className="font-bold text-amber-950 text-lg">Total:</p>
                  <p className="font-bold text-amber-700 text-xl">€{cartForOrder.total}</p>
                </div>
              </div>
            </div>
          )}

          <div className="grid md:grid-cols-3 gap-8">
            {/* Order steps */}
            <div className="space-y-4">
              <Card className="border-2 border-amber-100 bg-white">
                <CardContent className="p-6">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0">
                      <Package className="w-6 h-6 text-amber-700" />
                    </div>
                    <div>
                      <h3 className="font-bold text-amber-950 mb-1">1. Escolha</h3>
                      <p className="text-sm text-amber-700">Selecione o tipo e quantidade de doces</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="border-2 border-amber-100 bg-white">
                <CardContent className="p-6">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0">
                      <Calendar className="w-6 h-6 text-amber-700" />
                    </div>
                    <div>
                      <h3 className="font-bold text-amber-950 mb-1">2. Agende</h3>
                      <p className="text-sm text-amber-700">Mínimo 3 dias de antecedência</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="border-2 border-amber-100 bg-white">
                <CardContent className="p-6">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0">
                      <Users className="w-6 h-6 text-amber-700" />
                    </div>
                    <div>
                      <h3 className="font-bold text-amber-950 mb-1">3. Confirme</h3>
                      <p className="text-sm text-amber-700">Pagamento de 50% antecipado</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Order form */}
            <div className="md:col-span-2">
              <Card className="border-2 border-amber-200 shadow-xl">
                <CardContent className="p-8">
                  <form onSubmit={handleSubmit} className="space-y-6">
                    {/* Name */}
                    <div className="space-y-2">
                      <Label htmlFor="name" className="text-amber-950">
                        Nome Completo *
                      </Label>
                      <Input
                        id="name"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        placeholder="Seu nome"
                        className="border-amber-200 focus:border-amber-400 focus:ring-amber-400"
                        required
                      />
                    </div>

                    {/* Address */}
                    <div className="space-y-2">
                      <Label htmlFor="address" className="text-amber-950">
                        Morada
                      </Label>
                      <Input
                        id="address"
                        name="address"
                        type="text"
                        value={formData.address}
                        onChange={handleChange}
                        placeholder="Rua, número, localidade"
                        className="border-amber-200 focus:border-amber-400 focus:ring-amber-400"
                      />
                    </div>

                    {/* Phone */}
                    <div className="space-y-2">
                      <Label htmlFor="phone" className="text-amber-950">
                        Telefone / WhatsApp *
                      </Label>
                      <Input
                        id="phone"
                        name="phone"
                        type="tel"
                        value={formData.phone}
                        onChange={handleChange}
                        placeholder="+351 xxx xxx xxx"
                        className="border-amber-200 focus:border-amber-400 focus:ring-amber-400"
                        required
                      />
                    </div>

                    {/* Delivery Date */}
                    <div className="space-y-2">
                      <Label htmlFor="deliveryDate" className="text-amber-950">
                        Data de Entrega Desejada
                      </Label>
                      <Input
                        id="deliveryDate"
                        name="deliveryDate"
                        type="date"
                        value={formData.deliveryDate}
                        onChange={handleChange}
                        className="border-amber-200 focus:border-amber-400 focus:ring-amber-400"
                      />
                    </div>

                    {/* Message */}
                    <div className="space-y-2">
                      <Label htmlFor="message" className="text-amber-950">
                        Mensagem Adicional
                      </Label>
                      <Textarea
                        id="message"
                        name="message"
                        value={formData.message}
                        onChange={handleChange}
                        placeholder="Informações adicionais sobre seu pedido..."
                        rows={4}
                        className="border-amber-200 focus:border-amber-400 focus:ring-amber-400"
                      />
                    </div>

                    {/* Submit button */}
                    <Button 
                      type="submit"
                      className="w-full bg-amber-700 hover:bg-amber-800 text-white py-6 text-lg rounded-full transition-all duration-300 hover:scale-105 hover:shadow-xl"
                    >
                      Enviar Pedido
                      <Send className="ml-2 w-5 h-5" />
                    </Button>

                    <p className="text-sm text-amber-600 text-center">
                      * Campos obrigatórios
                    </p>
                  </form>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default OrderForm;
